import React from "react";
import { marked } from "marked"
import { configureFSS3 } from "src/context/AuthContextUtility"
import { Box, Header, SpaceBetween } from "@amzn/awsui-components-react";
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { getDocumentFromVault } from 'src/Ateam-BI/Vault/VaultAPIs';
import { VAULT_SERVICES } from 'src/Ateam-BI/Vault/constants';

const DEFAULT_CELL_WIDTH = 120;

const CELL_WIDTHS = {
  "no": 75,
  "section": 150,
  "severity": 120,
  "description": 200,
  "location": 200,
  "current_value": 200,
  "expected_value": 200,
  "impact": 300,
  "action_required": 400,
}

export const VIEW_OPTION_IDS = {
  EXCEPTION: "exception",
  ALL: "all",
}

export const VIEW_OPTIONS =[{
  text: "Exception only view",
  id: VIEW_OPTION_IDS.EXCEPTION
}, {
  text: "View all checks",
  id: VIEW_OPTION_IDS.ALL
}]

const EXCEPTION_STATUSES = [
  "important"
]

const formatHeader = (key) =>
  key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const buildAgGridTable = (tableData) => {
  if (!Array.isArray(tableData) || tableData.length === 0) {
    return (
      <Box>
        No Data
      </Box>
    );
  }

  const columns = Object.keys(tableData[0]);

  const columnDefs = columns.map((col) => ({
    field: col,
    headerName: formatHeader(col),
    sortable: true,
    resizable: true,
    wrapText: true,
    autoHeight: true,
    flex: 1,
    minWidth: CELL_WIDTHS[col] ?? DEFAULT_CELL_WIDTH,
  }));

  const defaultColDef = {
    sortable: true,
    resizable: true,
    wrapText: true,
    autoHeight: true
  };

  return (
    <div className="ag-theme-alpine firstpass-findings-grid" style={{ width: '100%' }}>
      <AgGridReact
        columnDefs={columnDefs}
        rowData={tableData}
        defaultColDef={defaultColDef}
        domLayout="autoHeight"
        suppressHorizontalScroll={false}
        enableCellTextSelection={true}
      />
    </div>
  );
};

const buildStructuredContent = ({ summary, summaryText, findingsTable, findingsHeader="Findings" }) => {
  return (
    <SpaceBetween direction="vertical" size="s">
      {summary && <Header variant="h3">Summary</Header>}
      {summary && buildAgGridTable([summary])}
      {summaryText && (
        <Box variant="p" margin={{ top: 's', bottom: 's' }}>
          {summaryText}
        </Box>
      )}
      {findingsTable && (
        <>
          <Header variant="h3">{findingsHeader}</Header>
          {buildAgGridTable(findingsTable)}
        </>
      )}
    </SpaceBetween>
  );
};

const getMarkdownResultContent = (jsonData) => {
  let resultsView = [];
  if (jsonData?.status_report?.result) {
    resultsView.push({
      'group': false,
      'header': 'Overall summary',
      'content': jsonData.status_report.result,
      'contentType': 'markdown'
    })
  }

  if (jsonData?.analysis) {
    resultsView.push({
      'group': false,
      'header': 'Analysis',
      'content': jsonData.analysis,
      'contentType': 'markdown'
    })
  }

  if (jsonData?.checks?.length > 0) {
    let checksView = []
    for (let check of jsonData.checks) {
          checksView.push({
              'content': check.analysis,
              'header': check.check_name,
              'contentType': 'markdown'
          })
    }
    resultsView.push({
      'group': true,
      'header': 'Checks',
      'childContent': checksView,
      'contentType': 'markdown'
    })
  }

  return resultsView;
}

export const getResultContent = (data, viewOption) => {
  if (!hasJSONStyleResults(data)) {
    return getMarkdownResultContent(data)
  }
  return getJSONResultContent(data, viewOption)
}

export const getJSONResultContent = (data, viewOption) => {  
  let resultsView = [];
  if (data?.status_report?.result) {
    const result = data.status_report.result;
    const findingsTable = result?.findings?.table;
    const findingsText = result?.findings?.text || '';
    const summary = result?.summary;

    const content = (findingsTable || summary || findingsText)
      ? buildStructuredContent({ summary, summaryText: findingsText, findingsTable, findingsHeader: "Overall Findings" })
      : (typeof result === 'string' ? result : JSON.stringify(result));

    const statusReportContent = (
      <SpaceBetween direction="vertical" size="s">
        <Header variant="h1">Status Report</Header>
        {typeof result === 'object' && result.total_sections !== undefined && buildAgGridTable([{
          total_sections: result.total_sections,
          sections_with_issues: result.sections_with_issues,
          total_important_findings: result.total_important_findings,
        }])}
        {content}
      </SpaceBetween>
    )

    resultsView.push({
      'group': false,
      'header': 'Overall summary',
      'content': statusReportContent,
      'sectionId': 'status-report',
      'contentType': "html"
    })
  }

  if (data?.checks?.length > 0) {
    let checksView = []
    for (let check of data.checks) {
      let viewCheck = structuredClone(check);
      if (viewOption === VIEW_OPTION_IDS.EXCEPTION) {
        const findings = viewCheck?.analysis?.findings;
        if (findings?.table) {
          findings.table = findings.table.filter(finding => EXCEPTION_STATUSES.includes(finding.severity));
        }
        if (findings) findings.text = null;
        if (viewCheck?.analysis) viewCheck.analysis.summary = null;
      }
      const checkTable = viewCheck?.analysis?.findings?.table;
      const checkText = viewCheck?.analysis?.findings?.text || '';
      const checkSummary = viewCheck?.analysis?.summary;

      const content = (checkTable || checkSummary || checkText)
        ? buildStructuredContent({ summary: checkSummary, summaryText: checkText, findingsTable: checkTable })
        : (typeof viewCheck.analysis === 'string' ? viewCheck.analysis : JSON.stringify(viewCheck.analysis));

      checksView.push({
          'content': content,
          'header': viewCheck.check_name,
          'checkId': viewCheck.prompt_id,
          'contentType': "html"
      })
    }
    resultsView.push({
      'group': true,
      'header': 'Checks',
      'childContent': checksView,
      'sectionId': 'checks',
      'contentType': "html"
    })
  }
  return resultsView;
}

export const getJSONSideNavInfo = (data) => {
  if (!hasJSONStyleResults(data)) return [];
  let checksSideNavItems = []
  if (data?.checks?.length > 0) {
    for (let check of data.checks) {
      checksSideNavItems.push({
          type: "link",
          text: check.check_name,
          href: `#/${check.prompt_id}`
      })
    }
  }
  return checksSideNavItems;
}



export const loadResultsFromS3 = async (data) => {
  configureFSS3()
  const fullPath = new URL(data?.response_s3_path)?.pathname?.substring(1);

  // Split into prefix (directory path) and filename
  const lastSlashIndex = fullPath.lastIndexOf('/');
  const prefix = fullPath.substring(0, lastSlashIndex + 1); // Include trailing slash
  const key = fullPath.substring(lastSlashIndex + 1); // Just the filename
  let arr = key.split('.')
  let contentType = arr[arr.length - 1]
  // const result = await Storage.get(key, { customPrefix: { public: prefix }, expires: 60, download: true });
  let document = await getDocumentFromVault(contentType, VAULT_SERVICES.FIRST_PASS_RESPONSES, {"file_path": `${prefix}${key}`})
   let fileURL = document?.fileUrl
   const response = await fetch(fileURL);
   const text = await response.text();
  // const text = await result.Body.text();
  const jsonData = JSON.parse(text);
  return jsonData
}

export const hasJSONStyleResults = (jsonData) => {
  if (!jsonData) {
    return null;
  }

  return Array.isArray(jsonData.checks) && jsonData.checks.length > 0 && jsonData.checks[0].analysis?.summary
}

export const parseStatusReportCounts = (jsonData) => {
  if (!jsonData || !hasJSONStyleResults(jsonData)) {
    return null;
  }

  let totalImportant = 0;
  let totalFyi = 0;
  
  for (const check of jsonData.checks) {
    const summary = check?.analysis?.summary;
    if (summary) {
      totalImportant += typeof summary.important_count === 'number' ? summary.important_count : 0;
      totalFyi += typeof summary.fyi_count === 'number' ? summary.fyi_count : 0;
    }
  }
  
  return {
    important: totalImportant,
    fyi: totalFyi
  };
};

export const getDownloadContent = async (data) => {
  const s3Data = await loadResultsFromS3(data)

  if (!hasJSONStyleResults(s3Data)) {
    return getMarkdownDownloadContent(s3Data)
  }

  return getJSONDownloadContent(s3Data)
}

export const getMarkdownDownloadContent = async (data) => {
  let mdContent = ''
  if (data?.status_report?.result) {
    mdContent += '------------------------\n'
    mdContent += '# ***Overall summary***  \n ------------------------  \n. \n'
    mdContent += `${data.status_report.result}\n`
  }

  if (data?.analysis) {
    mdContent += '------------------------\n'
    mdContent += '# ***Analysis***  \n ------------------------  \n. \n'
    mdContent += `${data.analysis}\n`
  }

  if (data?.checks?.length > 0) {
    mdContent += '------------------------\n'
    mdContent += '# ***Checks***  \n ------------------------  \n. \n'
    for (let check of data.checks) {
          mdContent += `${check.analysis}\n`
    }
    
  }
  const htmlContent = marked.parse(mdContent);
  return { 'html': htmlContent, 'markdown': mdContent }
}


export const getJSONDownloadContent = (data) => {
  let html = '';

  // Helper to build an HTML table from an array of objects
  const buildHtmlTable = (tableData) => {
    if (!Array.isArray(tableData) || tableData.length === 0) return '';
    const columns = Object.keys(tableData[0]);
    let table = '<table><thead><tr>';
    for (const col of columns) {
      table += `<th>${formatHeader(col)}</th>`;
    }
    table += '</tr></thead><tbody>';
    for (const row of tableData) {
      table += '<tr>';
      for (const col of columns) {
        const value = row[col] !== null && row[col] !== undefined ? row[col] : '';
        table += `<td>${value}</td>`;
      }
      table += '</tr>';
    }
    table += '</tbody></table>';
    return table;
  };

  // Status Report section
  if (data?.status_report?.result) {
    const result = data.status_report.result;
    html += '<h1>Status Report</h1>';

    // Summary metrics table
    if (typeof result === 'object' && result.total_sections !== undefined) {
      html += buildHtmlTable([{
        total_sections: result.total_sections,
        sections_with_issues: result.sections_with_issues,
        total_important_findings: result.total_important_findings,
      }]);
    }

    // Summary
    const summary = result?.summary;
    if (summary) {
      html += '<h3>Summary</h3>';
      html += buildHtmlTable([summary]);
    }

    // Findings text
    const findingsText = result?.findings?.text;
    if (findingsText) {
      html += `<p>${findingsText}</p>`;
    }

    // Findings table
    const findingsTable = result?.findings?.table;
    if (findingsTable && findingsTable.length > 0) {
      html += '<h3>Overall Findings</h3>';
      html += buildHtmlTable(findingsTable);
    }
  }

  // Checks section
  if (data?.checks?.length > 0) {
    html += '<h1>Checks</h1>';

    for (const check of data.checks) {
      html += `<h2>${check.check_name || 'Check'}</h2>`;

      const checkSummary = check?.analysis?.summary;
      if (checkSummary) {
        html += '<h3>Summary</h3>';
        html += buildHtmlTable([checkSummary]);
      }

      const checkText = check?.analysis?.findings?.text;
      if (checkText) {
        html += `<p>${checkText}</p>`;
      }

      const checkTable = check?.analysis?.findings?.table;
      if (checkTable && checkTable.length > 0) {
        html += '<h3>Findings</h3>';
        html += buildHtmlTable(checkTable);
      }

      // Fallback: if analysis is a string
      if (typeof check.analysis === 'string') {
        html += `<p>${check.analysis}</p>`;
      }
    }
  }

  return { html, markdown: null };
}

